import React, { useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import TabBar from '../TabBar';
import HistoryPanel from '../HistoryPanel';
import YardEventsPanel from '../YardEventsPanel';
import ReeferLogPanel from '../ReeferLogPanel';
import EdiEventsPanel from '../EdiEventsPanel';
import AdjustmentsPanel from '../AdjustmentsPanel';
import StoragePanel from '../StoragePanel';
import ReschedulesPanel from '../ReschedulesPanel';
import NotificationsPanel from '../NotificationsPanel';

// Table/column display-name maps for the Pro History sub-tab. Brought over
// from the old CMSHistoryDrawer wrapper, merged with V1's full %dbtable_map
// (main.pl bookingtrackertab) so Booking rows label correctly too.
const HISTORY_TABLE_NAMES: Record<string, string> = {
  ordersall: 'Order',
  legsall: 'Leg',
  legdispatch: 'Leg Dispatch',
  ordersallmore: 'Order (More)',
  notes: 'Notes',
  invoice: 'Invoice',
  invoice_lineitem: 'Invoice LI',
  troubleticket: 'Trouble Ticket',
  driverpay_lineitem: 'Driver Pay',
  masterrcv: 'Master RCV',
  rcv_overageshortage: 'Overage/Shortage',
  receiving: 'Receiving',
  template: 'Template',
  templatelegs: 'Leg',
  templateinvoice: 'Invoice',
  templateinvoice_lineitem: 'Invoice LI',
  rcv_units: 'Rcv Units',
  rcv_unitinfo: 'Rcv Unit Info',
  rcv_baylocations: 'Rcv Bay Loc',
  whsecheckin: 'Check In',
  whsecheckin_map: 'Check In Map',
  rcvchangelist: 'Change by Date',
  masterbl: 'Master BL',
  bl: 'BL',
  whseinstructions: 'Whse Instructions',
};

// Field display names for the Booking sub-tab (V1 %fielddef longnames for the
// five booking-tracker colnames).
const BOOKING_COLUMN_NAMES: Record<string, string> = {
  vessel: 'Vessel',
  bol: 'BOL/BKG/Ref#',
  firstrcvdate: 'First Rcv Date',
  cutoffdatetime: 'Cutoff Date/Time',
  documentcutoffdatetime: 'Doc Cutoff Date/Time',
};
export const HISTORY_COLUMN_NAMES: Record<string, string> = {
  legstatus: 'Driver Status',
  perdiemcalcdate: 'Per Diem Calc Date',
  numchassisdays: 'Chassis Days',
  lastupdatedatetime: 'Last Updated',
  lastchangedby: 'Last Change By',
  lastchangedatetime: 'Last Change Date',
  arrivaldatetime: 'Arrival Date/Time',
  departdatetime: 'Departure Date/Time',
  origarrivaldatetime: 'Origin Arrival',
  origdepartdatetime: 'Origin Departure',
  flagdispatch: 'Dispatch Flag',
  driver: 'Driver',
  origapptdate: 'Origin Appt Date',
  destapptdate: 'Dest Appt Date',
  container: 'Container',
  chassisid: 'Chassis ID',
  chassistype: 'Chassis Type',
  chassisprovider: 'Chassis Provider',
};

type Props = {
  /** Numeric order pronumber, a "T"-prefixed template pronumber, or null. */
  pronumber: number | string | null;
};

type SubTab =
  | 'pro'
  | 'edi'
  | 'adjustments'
  | 'storage'
  | 'reschedules'
  | 'notifications'
  | 'yard'
  | 'reefer'
  | 'booking';

export default function OrderHistoryView({ pronumber }: Props) {
  const [subTab, setSubTab] = useState<SubTab>('pro');
  const [refreshNonce, setRefreshNonce] = useState(0);

  // EDI/Adjustments/Storage/Reschedules/Notifications/Yard/Reefer are
  // physical-tracking tables keyed by the numeric CMS pronumber; templates
  // ("T…") have no such rows, so hand those panels null (they render empty)
  // rather than coercing the string. Pro History and Booking go through the
  // template-aware /orders/history/{pronumber} endpoint as a string.
  const numericPronumber = typeof pronumber === 'number' ? pronumber : null;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="relative mb-2">
        <TabBar
          tabs={[
            { id: 'pro', label: 'Pro History', current: subTab === 'pro' },
            { id: 'edi', label: 'EDI Events', current: subTab === 'edi' },
            { id: 'adjustments', label: 'Adjustments', current: subTab === 'adjustments' },
            { id: 'storage', label: 'Storage', current: subTab === 'storage' },
            { id: 'reschedules', label: 'Reschedules', current: subTab === 'reschedules' },
            { id: 'notifications', label: 'Notifications', current: subTab === 'notifications' },
            { id: 'yard', label: 'Yard Events', current: subTab === 'yard' },
            { id: 'reefer', label: 'Reefer Log', current: subTab === 'reefer' },
            { id: 'booking', label: 'Booking', current: subTab === 'booking' },
          ]}
          onTabChange={(id) => setSubTab(id as SubTab)}
          compact
          noBorder
        />
        <button
          onClick={() => setRefreshNonce((n) => n + 1)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          title="Refresh"
          type="button"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 min-h-0">
        {subTab === 'pro' && (
          <HistoryPanel
            endpoint={pronumber ? `/orders/history/${pronumber}` : null}
            tableDisplayNames={HISTORY_TABLE_NAMES}
            columnDisplayNames={HISTORY_COLUMN_NAMES}
            active
            refreshNonce={refreshNonce}
          />
        )}
        {subTab === 'edi' && (
          <EdiEventsPanel
            pronumber={numericPronumber}
            active
            refreshNonce={refreshNonce}
          />
        )}
        {subTab === 'adjustments' && (
          <AdjustmentsPanel
            pronumber={numericPronumber}
            active
            refreshNonce={refreshNonce}
          />
        )}
        {subTab === 'storage' && (
          <StoragePanel
            pronumber={numericPronumber}
            active
            refreshNonce={refreshNonce}
          />
        )}
        {subTab === 'reschedules' && (
          <ReschedulesPanel
            pronumber={numericPronumber}
            active
            refreshNonce={refreshNonce}
          />
        )}
        {subTab === 'notifications' && (
          <NotificationsPanel
            pronumber={numericPronumber}
            active
            refreshNonce={refreshNonce}
          />
        )}
        {subTab === 'yard' && (
          <YardEventsPanel
            pronumber={numericPronumber}
            active
            refreshNonce={refreshNonce}
          />
        )}
        {subTab === 'reefer' && (
          <ReeferLogPanel
            pronumber={numericPronumber}
            active
            refreshNonce={refreshNonce}
          />
        )}
        {subTab === 'booking' && (
          <HistoryPanel
            endpoint={
              pronumber
                ? `/orders/history/${pronumber}?colnames=vessel,bol,firstrcvdate,cutoffdatetime,documentcutoffdatetime`
                : null
            }
            cardTitle="Booking"
            settingsKey="pro-history-booking"
            tableDisplayNames={HISTORY_TABLE_NAMES}
            columnDisplayNames={BOOKING_COLUMN_NAMES}
            active
            refreshNonce={refreshNonce}
          />
        )}
      </div>
    </div>
  );
}
